import React, { useState, useEffect, useRef } from 'react';

// ----------------------------------------------------------------------
// --- EXPLICIT IMAGE IMPORTS (FIXED) ---
// ----------------------------------------------------------------------
import Interior1 from '../../../assets/Images/Interior80.jpg'; 
import Interior2 from '../../../assets/Images/Interior87.jpg'; 
import Interior3 from '../../../assets/Images/Interior67.jpg'; 
import Interior4 from '../../../assets/Images/Interior33.jpg'; 
import Interior5 from '../../../assets/Images/Interior43.jpg'; 
import Interior6 from '../../../assets/Images/Interior55.jpg'; 
import Interior7 from '../../../assets/Images/Interior62.jpg'; 
import Interior8 from '../../../assets/Images/Interior77.jpg'; 
import Interior9 from '../../../assets/Images/Interior15.jpg'; 


// Map all imported images into a single, accessible array
const interiorImages = [
    Interior1, Interior2, Interior3, Interior4, Interior5, Interior6, Interior7, Interior8, Interior9 
];

// Helper to get an image source and a generic alt text
const getImage = (index) => {
    const safeIndex = index % interiorImages.length;
    return {
        src: interiorImages[safeIndex],
        alt: `D Red Interior Design Example ${safeIndex + 1}`
    };
};

// --- AnimateOnScroll Component (Kept the same) ---
const AnimateOnScroll = ({ children, direction = 'up', delay = 0, className = '' }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);
    const directionClasses = {
        up: 'translate-y-20', down: '-translate-y-20',
        left: '-translate-x-20', right: 'translate-x-20',
    };
    const initialClasses = `opacity-0 ${directionClasses[direction]}`;
    const finalClasses = 'opacity-100 translate-y-0 translate-x-0';
    
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        }, { threshold: 0.1 }); 
        const currentRef = ref.current;
        // WARNING FIX: Copy ref.current to a local variable inside the effect
        // const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef) };
    }, []);

    return (
        <div 
            ref={ref} 
            className={`transition-all duration-1000 ease-out ${className} ${isVisible ? finalClasses : initialClasses}`} 
            style={{ transitionDelay: `${delay}ms` }} 
        >
            {children}
        </div>
    );
};

// --- Main Component: AboutUsPillars (MODIFIED) ---
const AboutUsPillars = () => {
    // Defines common classes for hover effects on images
    const imageHoverClasses = "transition duration-500 ease-in-out transform hover:scale-[1.03] hover:shadow-2xl hover:shadow-red-500/40";
    
    return (
        <section id="about-us-pillars" className="py-24 bg-gray-50 text-gray-800">
            <div className="container mx-auto px-6 max-w-7xl">
                
                {/* --- Introduction & Core Mission (Uses Interior 1) --- */}
                <AnimateOnScroll direction="up">
                    <div className="text-center mb-16 pb-12 border-b border-gray-200">
                        <h1 className="text-6xl md:text-7xl headline-font font-extrabold text-red-700 mb-4">D Red Interiors: Our Story</h1>
                        <p className="text-xl mt-4 max-w-4xl mx-auto text-gray-700 leading-relaxed">
                            At D Red Interiors in Madipakkam, Chennai, we are passionate specialists in architectural and interior design. Our foundation is built on a simple promise: to deliver unrivaled excellence in every project, blending our wealth of experience with a deep, uncompromising commitment to innovative and sustainable design solutions.
                        </p>
                    </div>
                </AnimateOnScroll>

                <div className="grid md:grid-cols-2 gap-16 items-start mb-24 pb-16 border-b border-red-200">
                    <AnimateOnScroll direction="right">
                        {/* Image: Interior 1 (FIXED TO Interior80.jpg) with Hover */}
                        <img 
                            src={getImage(0).src} 
                            className={`w-full h-[450px] object-cover rounded-xl shadow-2xl border-4 border-red-600 ${imageHoverClasses}`} 
                            alt={getImage(0).alt}
                        />
                    </AnimateOnScroll>
                    <AnimateOnScroll direction="left" className="pt-6"> {/* Added padding for vertical alignment */}
                        {/* Text Block Hover Effect */}
                        <div className="p-4 rounded-lg transition duration-300 hover:bg-gray-100/50">
                            <h2 className="text-4xl headline-font font-bold text-red-600 mb-4">Our Vision: Storytelling Through Space</h2>
                            <p className="mt-4 text-lg text-gray-700 leading-7">
                                Our core mission is to redefine the standards of the industry. We achieve this by crafting innovative, customized solutions that perfectly align with the unique tastes and practical needs of our clientele. We aim to elevate living and working spaces into environments that are deeply personal and highly functional.
                            </p>
                            {/* Accent Text Hover Effect */}
                            <p className="mt-6 text-xl font-semibold text-red-700 border-l-4 border-red-600 pl-4 bg-red-50 p-4 shadow-inner transition duration-300 hover:shadow-md hover:border-red-700">
                                "We believe that every space tells a story a narrative of who you are, what you value, and how you live. Our commitment is to help you tell that story in the most compelling, aesthetically pleasing, and functional way possible."
                            </p>
                        </div>
                    </AnimateOnScroll>
                </div>
                
                {/* ------------------------------------------------------------- */}
                {/* --- SECTION: OUR CORE TENETS OF DESIGN --- */}
                {/* ------------------------------------------------------------- */}
                <AnimateOnScroll direction="up" delay={100}>
                    <div className="text-center mb-20 bg-gray-100 p-10 rounded-xl border-b-4 border-red-600 shadow-md transition duration-500 hover:shadow-red-300/50">
                        <h2 className="text-4xl md:text-5xl headline-font font-bold text-gray-900">Our Core Tenets of Design</h2>
                        <p className="text-xl mt-4 max-w-4xl mx-auto text-gray-700">
                            We believe that truly great design is about engineering experiences, not just decorating rooms. Our philosophy is the convergence of aesthetics, functionality, and sustainability, ensuring the spaces we create are beautiful, practical, and enduring investments.
                        </p>
                    </div>
                </AnimateOnScroll>

                {/* --- Tenet 1: Functionality (Uses Interior 2) --- */}
                <div className="grid md:grid-cols-2 gap-16 items-center mb-20 group">
                    <AnimateOnScroll direction="right">
                        {/* Image: Interior 2 (Interior87.jpg) with Hover */}
                        <img 
                            src={getImage(1).src} 
                            className={`w-full h-96 object-cover rounded-xl shadow-2xl border border-gray-300 ${imageHoverClasses}`} 
                            alt={getImage(1).alt} 
                        />
                    </AnimateOnScroll>
                    <AnimateOnScroll direction="left" className="p-4 rounded-lg transition duration-300 group-hover:bg-gray-100/70">
                        {/* Text Hover: Scale/Color change */}
                        <h3 className="text-3xl headline-font font-bold text-red-600 mb-3 transition duration-300 group-hover:text-red-700 group-hover:scale-[1.01]">Tenet 1: The Power of Practicality and Flow</h3>
                        <p className="mt-4 text-lg text-gray-700 leading-7">
                            We analyze how you use your space to prioritize maximum efficiency and ergonomic comfort. From the intelligent layout of a modular kitchen to the seamless flow of a small office interior, every design element is engineered to support and enhance your daily routines. Our solutions result in sophisticated, clutter-free living and streamlined workflow environments.
                        </p>
                    </AnimateOnScroll>
                </div>

                {/* --- Tenet 2: Holistic Expertise (Uses Interior 3) --- */}
                <div className="grid md:grid-cols-2 gap-16 items-center mb-20 group">
                    <AnimateOnScroll direction="right" className="order-last md:order-first p-4 rounded-lg transition duration-300 group-hover:bg-gray-100/70">
                        {/* Text Hover: Scale/Color change */}
                        <h3 className="text-3xl headline-font font-bold text-red-600 mb-3 transition duration-300 group-hover:text-red-700 group-hover:scale-[1.01]">Tenet 2: Blending Aesthetics with Cutting-Edge Technology</h3>
                        <p className="mt-4 text-lg text-gray-700 leading-7">
                            Our team possesses expertise in both architectural planning and advanced interior detailing, guaranteeing a seamless, holistic approach from structural concept to final finish. We stay ahead of the latest design trends and smart home technologies to deliver innovative, future-proof spaces. We empower our clients with a crystal-clear vision of the final project using advanced 3D visualization tools before any ground is broken.
                        </p>
                    </AnimateOnScroll>
                    <AnimateOnScroll direction="left">
                        {/* Image: Interior 3 (FIXED TO Interior67.jpg) with Hover */}
                        <img 
                            src={getImage(2).src} 
                            className={`w-full h-96 object-cover rounded-xl shadow-2xl border border-gray-300 ${imageHoverClasses}`} 
                            alt={getImage(2).alt} 
                        />
                    </AnimateOnScroll>
                </div>

                {/* --- Tenet 3: Commitment to Quality (Uses Interior 4) --- */}
                <div className="grid md:grid-cols-2 gap-16 items-center mb-20 group">
                    <AnimateOnScroll direction="right">
                        {/* Image: Interior 4 (Interior33.jpg) with Hover */}
                        <img 
                            src={getImage(3).src} 
                            className={`w-full h-96 object-cover rounded-xl shadow-2xl border border-gray-300 ${imageHoverClasses}`} 
                            alt={getImage(3).alt} 
                        />
                    </AnimateOnScroll>
                    <AnimateOnScroll direction="left" className="p-4 rounded-lg transition duration-300 group-hover:bg-gray-100/70">
                        {/* Text Hover: Scale/Color change */}
                        <h3 className="text-3xl headline-font font-bold text-red-600 mb-3 transition duration-300 group-hover:text-red-700 group-hover:scale-[1.01]">Tenet 3: Superior Quality, Enduring Value</h3>
                        <p className="mt-4 text-lg text-gray-700 leading-7">
                            Quality is non-negotiable and is evident in every detail. It starts with the selection of premium, sustainable materials, continues through the precise execution by our skilled tradesmen, and is finalized with the reliability of our trusted brand partners. Our meticulous attention ensures that the result is not just a beautiful space, but a superior, long term investment that is durable, timeless, and certified to stand the test of time.
                        </p>
                    </AnimateOnScroll>
                </div>

                {/* --- Tenet 4: Customization (Uses Interior 5) --- */}
                <div className="grid md:grid-cols-2 gap-16 items-center mb-24 group">
                    <AnimateOnScroll direction="right" className="order-last md:order-first p-4 rounded-lg transition duration-300 group-hover:bg-gray-100/70">
                        {/* Text Hover: Scale/Color change */}
                        <h3 className="text-3xl headline-font font-bold text-red-600 mb-3 transition duration-300 group-hover:text-red-700 group-hover:scale-[1.01]">Tenet 4: Tailored to Your Unique Personality</h3>
                        <p className="mt-4 text-lg text-gray-700 leading-7">
                            We firmly reject generic, off-the-shelf designs. We begin every project by deeply understanding your unique personality, lifestyle, and aspirations. Our bespoke solutions whether for a luxury bedroom, a customized home office, or specialized garden design ensure that the final environment is a true, authentic, and exclusive reflection of you. We pride ourselves on creating spaces that feel inherently yours.
                        </p>
                    </AnimateOnScroll>
                    <AnimateOnScroll direction="left">
                        {/* Image: Interior 5 (FIXED TO Interior43.jpg) with Hover */}
                        <img 
                            src={getImage(4).src} 
                            className={`w-full h-96 object-cover rounded-xl shadow-2xl border border-gray-300 ${imageHoverClasses}`} 
                            alt={getImage(4).alt} 
                        />
                    </AnimateOnScroll>
                </div>
                
                {/* ------------------------------------------------------------- */}
                {/* --- SECTION: Leadership and Vision (Updated with Proprietor Name) --- */}
                {/* ------------------------------------------------------------- */}
                <div className="mt-16 pt-16 border-t-2 border-red-600 text-center">
                    <AnimateOnScroll direction="up">
                        <h2 className="text-4xl md:text-5xl headline-font font-bold text-red-600 mb-8">Leadership and Commitment to Excellence</h2>
                    </AnimateOnScroll>

                    <AnimateOnScroll direction="up" delay={200} className="max-w-4xl mx-auto">
                        {/* Proprietor Name and Title Added */}
                        <h3 className="text-3xl headline-font font-bold text-gray-900 transition duration-300 hover:text-red-600 mb-2">
                            Mr. Nandha Kumar: Founder & CEO
                        </h3>
                        <p className="text-xl font-semibold text-red-700 mb-4">A Deep Passion for Design Excellence</p>

                        <p className="mt-4 text-lg text-gray-700 leading-7">
                            Mr. Nandha Kumar established D Red Interiors with the fundamental belief that design must harmonize aesthetics with ethics. His deep understanding of architectural principles and contemporary interior demands ensures that every project not only dazzles visually but also adheres to the highest standards of structural integrity and sustainability. His leadership drives our team's commitment to delivering superior results in Madipakkam and across Chennai.
                        </p>
                        <p className="mt-6 text-lg border-l-4 border-red-600 pl-4 italic bg-red-50 p-3 shadow-sm transition duration-300 hover:bg-red-100">
                            "Our commitment is to excellence and attention to detail. We do not stop until the final environment is both a beautiful representation of our client's vision and an enduring, functional reality."
                        </p>
                    </AnimateOnScroll>
                </div>

            </div>
        </section>
    );
};

export default AboutUsPillars;
